import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { MetaChipTone } from "../../../../components/UIToolkit/Badges/MetaChip";
import type { Department, DepartmentJobShiftTimes } from "../../../../contexts/AuthProvider";
import type { TranslationKeys } from "../../../../i18n/types";
import type {
	EmployeeBucket,
	FailedRecurringShift,
	JobShiftHours,
	PatternKind,
	RecurringCandidateRow,
	RecurringCreationResult,
	RecurringFlag,
	RecurringSelectedOutcome,
	RequestedPattern,
	SaveRecurringShiftHoursRequest,
	SaveRecurringShiftHoursResult,
	ShiftHoursSaveSkipReason,
	ShiftHoursSlot,
	ShiftHoursSource,
	ShiftSource,
	SkipReason,
	WireDate,
} from "../../../../services/backend.service";

/**
 * Presentation for the "Calculate recurring" review screen (DAT-774).
 *
 * Every enum the backend can return is given a label AND an explanation, because the screen's job is
 * to let someone approve or reject a proposal — a bare `scheduleUnreliable` chip tells a reviewer
 * that something is wrong but not whether it matters. Kept apart from the components so the wording
 * is reviewable in one place and no label is invented inline.
 */

export const NOT_SET = "—";

/** Shown when a proposal's score is below this, mirroring the design's own guidance bands. */
export const SCORE_GUIDANCE: ReadonlyArray<{ atLeast: number; text: string }> = [
	{ atLeast: 1, text: "Only perfect patterns — every generated shift matches history exactly." },
	{ atLeast: 0.8, text: "About 1 in 5 generated shifts may be wrong." },
	{ atLeast: 0.6, text: "About 2 in 5 generated shifts may be wrong — plan a manual pass." },
	{ atLeast: 0, text: "Below 0.6 most proposals are guesses. Expect to correct the schedule by hand." },
];

export function scoreGuidance(minimumScore: number): string {
	// `find` on a descending list gives the tightest band the value still clears.
	return SCORE_GUIDANCE.find((band) => minimumScore >= band.atLeast)?.text ?? SCORE_GUIDANCE[SCORE_GUIDANCE.length - 1].text;
}

export const REQUESTED_PATTERN_LABELS: Record<RequestedPattern, string> = {
	auto: "Auto — let the fit choose",
	"4-2": "4 on / 2 off",
	everyWeek: "Every week",
	twoWeekly: "Every 2 weeks",
	fourWeekly: "Every 4 weeks",
	custom: "Other — set the number of weeks",
};

/** Ordered for the dropdown: `auto` first because it is the default and the right answer most of the time. */
export const REQUESTED_PATTERN_ORDER: RequestedPattern[] = ["auto", "4-2", "everyWeek", "twoWeekly", "fourWeekly", "custom"];

/** Default weeks for a custom cycle: the length people ask for most, and a sane starting number. */
export const DEFAULT_CUSTOM_CYCLE_WEEKS = 6;

export const PATTERN_KIND_LABELS: Record<PatternKind, string> = {
	rotation: "Rotation",
	everyWeek: "Every week",
	twoWeekly: "Every 2 weeks",
	fourWeekly: "Every 4 weeks",
	custom: "Custom cycle",
};

/** A pattern kind as text, naming the real cycle length when the kind alone doesn't carry it. */
export function patternKindLabel(kind: PatternKind | null, cycleWeeks: number | null): string {
	if (!kind) return NOT_SET;
	if (kind === "custom") return cycleWeeks ? `Every ${cycleWeeks} weeks` : PATTERN_KIND_LABELS.custom;
	return PATTERN_KIND_LABELS[kind];
}

export const SHIFT_SOURCE_LABELS: Record<ShiftSource, string> = {
	schedule: "Schedule",
	punches: "Punches",
	both: "Schedule + punches",
};

export const BUCKET_LABELS: Record<EmployeeBucket, string> = {
	ready: "Ready",
	needsReview: "Needs review",
	skipped: "Skipped",
};

const BUCKET_DESCRIPTION_KEYS = {
	ready: "calculateRecurring.bucket.ready.description",
	needsReview: "calculateRecurring.bucket.needsReview.description",
	skipped: "calculateRecurring.bucket.skipped.description",
} as const satisfies Record<EmployeeBucket, TranslationKeys>;

export const bucketDescription = (bucket: EmployeeBucket, t: TFunction): string => t(BUCKET_DESCRIPTION_KEYS[bucket]);

const FLAG_LABEL_KEYS = {
	betterPatternFound: "calculateRecurring.flag.betterPatternFound.label",
	singleSource: "calculateRecurring.flag.singleSource.label",
	sourcesNotComparable: "calculateRecurring.flag.sourcesNotComparable.label",
	scheduleUnreliable: "calculateRecurring.flag.scheduleUnreliable.label",
	patternChanged: "calculateRecurring.flag.patternChanged.label",
	limitedData: "calculateRecurring.flag.limitedData.label",
	ptoUnavailable: "calculateRecurring.flag.ptoUnavailable.label",
} as const satisfies Record<RecurringFlag, TranslationKeys>;

const FLAG_DESCRIPTION_KEYS = {
	betterPatternFound: "calculateRecurring.flag.betterPatternFound.description",
	singleSource: "calculateRecurring.flag.singleSource.description",
	sourcesNotComparable: "calculateRecurring.flag.sourcesNotComparable.description",
	scheduleUnreliable: "calculateRecurring.flag.scheduleUnreliable.description",
	patternChanged: "calculateRecurring.flag.patternChanged.description",
	limitedData: "calculateRecurring.flag.limitedData.description",
	ptoUnavailable: "calculateRecurring.flag.ptoUnavailable.description",
} as const satisfies Record<RecurringFlag, TranslationKeys>;

export const flagLabel = (flag: RecurringFlag, t: TFunction): string => t(FLAG_LABEL_KEYS[flag]);

export const flagDescription = (flag: RecurringFlag, t: TFunction): string => t(FLAG_DESCRIPTION_KEYS[flag]);

const SOURCE_WITHOUT_AGREEMENT_DESCRIPTION_KEYS = {
	schedule: "calculateRecurring.source.scheduleOnly.description",
	punches: "calculateRecurring.source.punchesOnly.description",
	both: "calculateRecurring.source.notComparable.description",
} as const satisfies Record<ShiftSource, TranslationKeys>;

const SOURCE_DESCRIPTION_KEYS = {
	scheduleTrusted: "calculateRecurring.source.scheduleTrusted.description",
	punchesPreferred: "calculateRecurring.source.punchesPreferred.description",
	merged: "calculateRecurring.source.merged.description",
	scheduleTrustedWithPunchCoverage: "calculateRecurring.source.scheduleTrustedWithPunchCoverage.description",
	punchesPreferredWithScheduleCoverage: "calculateRecurring.source.punchesPreferredWithScheduleCoverage.description",
} as const satisfies Record<string, TranslationKeys>;

// Mirrors the backend agreement bands that choose which source supplies comparable dates.
const SCHEDULE_TRUSTED_MATCH_THRESHOLD = 0.85;
const SOURCE_MERGE_MATCH_THRESHOLD = 0.5;

export const shiftSourceDescription = (source: ShiftSource, sourceMatch: number | null, t: TFunction): string => {
	if (sourceMatch === null) return t(SOURCE_WITHOUT_AGREEMENT_DESCRIPTION_KEYS[source]);
	if (source === "schedule") return t(SOURCE_DESCRIPTION_KEYS.scheduleTrusted);
	if (source === "punches") return t(SOURCE_DESCRIPTION_KEYS.punchesPreferred);
	if (sourceMatch > SCHEDULE_TRUSTED_MATCH_THRESHOLD) return t(SOURCE_DESCRIPTION_KEYS.scheduleTrustedWithPunchCoverage);
	if (sourceMatch >= SOURCE_MERGE_MATCH_THRESHOLD) return t(SOURCE_DESCRIPTION_KEYS.merged);
	return t(SOURCE_DESCRIPTION_KEYS.punchesPreferredWithScheduleCoverage);
};

export const FLAG_TONES: Record<RecurringFlag, MetaChipTone> = {
	betterPatternFound: "warning",
	singleSource: "info",
	sourcesNotComparable: "info",
	scheduleUnreliable: "warning",
	patternChanged: "warning",
	limitedData: "warning",
	ptoUnavailable: "info",
};

const SKIP_REASON_LABEL_KEYS = {
	noStandingPattern: "calculateRecurring.skip.noStandingPattern.label",
	notEnoughData: "calculateRecurring.skip.notEnoughData.label",
	noData: "calculateRecurring.skip.noData.label",
	noPrimaryJob: "calculateRecurring.skip.noPrimaryJob.label",
	notInDepartment: "calculateRecurring.skip.notInDepartment.label",
} as const satisfies Record<SkipReason, TranslationKeys>;

const SKIP_REASON_DESCRIPTION_KEYS = {
	noStandingPattern: "calculateRecurring.skip.noStandingPattern.description",
	notEnoughData: "calculateRecurring.skip.notEnoughData.description",
	noData: "calculateRecurring.skip.noData.description",
	noPrimaryJob: "calculateRecurring.skip.noPrimaryJob.description",
	notInDepartment: "calculateRecurring.skip.notInDepartment.description",
} as const satisfies Record<SkipReason, TranslationKeys>;

export const skipReasonLabel = (reason: SkipReason, t: TFunction): string => t(SKIP_REASON_LABEL_KEYS[reason]);
export const skipReasonDescription = (reason: SkipReason, t: TFunction): string => t(SKIP_REASON_DESCRIPTION_KEYS[reason]);

export const SHIFT_HOURS_SOURCE_LABELS: Record<ShiftHoursSource, string> = {
	confirmed: "Confirmed by you",
	configured: "Department configuration",
	departmentDefault: "Department default",
	inferred: "Inferred from history",
};

export const SHIFT_HOURS_SOURCE_DESCRIPTIONS: Record<ShiftHoursSource, string> = {
	confirmed: "You edited these and sent them back, so they were used verbatim.",
	configured: "Read from this job's own shift-time configuration.",
	departmentDefault: "This job has no configuration of its own, so the department's default times were used.",
	inferred: "Guessed from the shifts actually worked, because nothing was configured. Worth checking.",
};

export const SHIFT_HOURS_SOURCE_TONES: Record<ShiftHoursSource, MetaChipTone> = {
	confirmed: "success",
	configured: "info",
	departmentDefault: "neutral",
	inferred: "warning",
};

/**
 * A `yyyy-MM-dd` wire field as display text.
 *
 * These reach us as Luxon `DateTime`, not strings — `axiosInstance` runs `convertIsoDates` over
 * every response. Rendering one straight into JSX throws React error #31 and unmounts the screen,
 * which is how this shipped to the preview: the types said `string`, the fixture used strings, and
 * only a real API response went through the interceptor. Handles both forms so it stays correct for
 * fixtures and any caller that bypasses axios.
 */
export function formatIsoDate(value: WireDate | null): string {
	if (!value) return NOT_SET;
	const date = typeof value === "string" ? DateTime.fromISO(value) : value;
	return date.isValid ? date.toFormat("MMM d, yyyy") : NOT_SET;
}

/** "07:00:00" → "07:00". Times arrive from the backend as full ISO times. */
export function formatClock(time: string): string {
	return time.slice(0, 5);
}

export function formatSlot(slot: ShiftHoursSlot): string {
	return `${formatClock(slot.start)}-${formatClock(slot.end)}`;
}

export function formatSlots(slots: ShiftHoursSlot[]): string {
	return slots.length ? slots.map(formatSlot).join(", ") : NOT_SET;
}

/** A share (0-1) as a whole percentage. `null` stays `—` rather than becoming a misleading 0%. */
export function formatShare(value: number | null): string {
	return value === null ? NOT_SET : `${Math.round(value * 100)}%`;
}

/**
 * The days a proposal lands on, in the most compact honest form.
 *
 * A rotation has no weekdays — it drifts — so its work/rest counts are the answer. A cyclical
 * pattern with more than one week has to name the week as well, or "Mon, Tue" would read as every
 * week when it means week 2 only.
 */
export function formatPatternDays(row: RecurringCandidateRow): string {
	if (row.patternKind === "rotation") {
		return row.workDays !== null && row.restDays !== null ? `${row.workDays} on / ${row.restDays} off` : NOT_SET;
	}
	if (row.cycleSlots.length > 0) {
		const cycleWeeks = new Set(row.cycleSlots.map(([week]) => week));
		if (cycleWeeks.size > 1 || row.patternKind === "twoWeekly" || row.patternKind === "fourWeekly") {
			const byWeek = new Map<number, string[]>();
			for (const [week, weekday] of row.cycleSlots) {
				byWeek.set(week, [...(byWeek.get(week) ?? []), weekday]);
			}
			return [...byWeek.entries()]
				.sort(([a], [b]) => a - b)
				.map(([week, days]) => `W${week + 1}: ${days.join(", ")}`)
				.join(" · ");
		}
	}
	return row.weekdays.length ? row.weekdays.join(", ") : NOT_SET;
}

/** Only `ready` and `needsReview` rows can be approved — `skipped` rows have no pattern to create. */
export function isApprovable(row: RecurringCandidateRow): boolean {
	return row.bucket !== "skipped";
}

const sameSlots = (a: ShiftHoursSlot[], b: ShiftHoursSlot[]): boolean =>
	a.length === b.length && a.every((slot, index) => slot.start === b[index].start && slot.end === b[index].end);

/**
 * Jobs whose hours the operator has changed since the run that derived them.
 *
 * Shared by the panel (which labels and counts them) and the approval gate (which refuses while any
 * are outstanding). One definition, because "edited" deciding differently in those two places is how
 * you end up able to approve a correction that was never applied.
 */
export function editedJobNames(derived: JobShiftHours[], edits: Record<string, ShiftHoursSlot[]>): string[] {
	return derived.filter((entry) => entry.jobName in edits && !sameSlots(edits[entry.jobName], entry.slots)).map((entry) => entry.jobName);
}

/**
 * The hours the panel is SHOWING for a job: the operator's edit if there is one, else what the run
 * derived. Not a fallback — an absent edit means "unchanged", so the derived hours are what is on
 * screen, and what a save must store.
 */
export function shownSlots(entry: JobShiftHours, edits: Record<string, ShiftHoursSlot[]>): ShiftHoursSlot[] {
	return edits[entry.jobName] ?? entry.slots;
}

/** Why a save could not write a job; `null` when it could. */
export type ShiftHoursSaveLockReason = "configured" | "noHours" | "zeroLength";

/**
 * The single rule for whether a save could write a job, given the hours on screen for it.
 *
 * A save never overwrites an existing department row. The other two reasons mirror request-wide
 * server rejections — a job with no hours, or a shift that starts and ends at the same time (which
 * inference can produce) — so sending one would fail the save for every selected job, with an error
 * that names none of them. Locking the row instead says which job needs fixing.
 */
export function shiftHoursSaveLockReason(entry: JobShiftHours, slots: ShiftHoursSlot[]): ShiftHoursSaveLockReason | null {
	if (entry.hasDepartmentRow) return "configured";
	if (slots.length === 0) return "noHours";
	if (slots.some((slot) => slot.start === slot.end)) return "zeroLength";
	return null;
}

export function isShiftHoursSavable(entry: JobShiftHours, edits: Record<string, ShiftHoursSlot[]>): boolean {
	return shiftHoursSaveLockReason(entry, shownSlots(entry, edits)) === null;
}

/** Translation key for the hint on a locked "save to department" checkbox. */
export const SHIFT_HOURS_SAVE_LOCK_REASON_KEYS: Record<ShiftHoursSaveLockReason, string> = {
	configured: "onboarding_load.recurring_shift_hours_save_checkbox_locked",
	noHours: "onboarding_load.recurring_shift_hours_save_checkbox_no_hours",
	zeroLength: "onboarding_load.recurring_shift_hours_save_checkbox_zero_length",
};

/**
 * Jobs pre-ticked for "save to department": everything the operator confirmed or corrected — the
 * hours they actually reviewed — that a save could still write. Inferred and default hours are left
 * unticked: nobody has vouched for them yet.
 */
export function defaultShiftHoursSaveSelection(derived: JobShiftHours[], edits: Record<string, ShiftHoursSlot[]>): string[] {
	const edited = new Set(editedJobNames(derived, edits));
	return derived
		.filter((entry) => isShiftHoursSavable(entry, edits) && (entry.source === "confirmed" || edited.has(entry.jobName)))
		.map((entry) => entry.jobName);
}

/** Jobs that became edited in this change and can still be saved — ticked for the operator as they edit. */
export function newlyEditedSavableJobs(
	derived: JobShiftHours[],
	previousEdits: Record<string, ShiftHoursSlot[]>,
	nextEdits: Record<string, ShiftHoursSlot[]>,
): string[] {
	const alreadyEdited = new Set(editedJobNames(derived, previousEdits));
	const savable = new Set(derived.filter((entry) => isShiftHoursSavable(entry, nextEdits)).map((entry) => entry.jobName));
	return editedJobNames(derived, nextEdits).filter((jobName) => !alreadyEdited.has(jobName) && savable.has(jobName));
}

/** The request body for the selected jobs, carrying exactly the hours on screen. */
export function shiftHoursSavePayload(
	derived: JobShiftHours[],
	edits: Record<string, ShiftHoursSlot[]>,
	selected: readonly string[],
): SaveRecurringShiftHoursRequest["jobs"] {
	const chosen = new Set(selected);
	return derived
		.filter((entry) => chosen.has(entry.jobName) && isShiftHoursSavable(entry, edits))
		.map((entry) => ({ jobName: entry.jobName, slots: shownSlots(entry, edits) }));
}

/**
 * The derived entries after a save, with every job that now has a department row locked.
 *
 * A job skipped as `alreadyConfigured` is locked too: the row exists (someone saved it between the
 * run and this click), so ticking it again could only be skipped again.
 */
export function withSavedDepartmentRows(derived: JobShiftHours[], result: SaveRecurringShiftHoursResult): JobShiftHours[] {
	const nowConfigured = new Set([
		...result.saved.map((entry) => entry.jobName),
		...result.skipped.filter((entry) => entry.reason === "alreadyConfigured").map((entry) => entry.jobName),
	]);
	return derived.map((entry) => (nowConfigured.has(entry.jobName) ? { ...entry, hasDepartmentRow: true } : entry));
}

/**
 * The in-memory department after a save, with its per-job table replaced by the server's post-save
 * copy — but only when it is the department that was saved. The chosen department can change while
 * the request is in flight, and another department's table must never be overwritten with this one.
 */
export function withSavedJobShiftTimes<D extends Pick<Department, "department_id" | "jobShiftTimes">>(
	department: D,
	savedDepartmentId: string,
	jobShiftTimes: DepartmentJobShiftTimes[],
): D {
	return department.department_id === savedDepartmentId ? { ...department, jobShiftTimes } : department;
}

/** Translation key for each reason a job was not saved. */
export const SHIFT_HOURS_SAVE_SKIP_REASON_KEYS: Record<ShiftHoursSaveSkipReason, string> = {
	alreadyConfigured: "onboarding_load.recurring_shift_hours_save_reason_already_configured",
	jobNotEnabled: "onboarding_load.recurring_shift_hours_save_reason_job_not_enabled",
	unknownJob: "onboarding_load.recurring_shift_hours_save_reason_unknown_job",
};

/**
 * Why an approval failed, in the operator's terms.
 *
 * The backend returns the insert's raw error per employee. The commonest one by far is a shift
 * conflict, and its meaning is specific and actionable: the employee ALREADY has a template, and the
 * shifts it generated occupy the slots the new one wants — so the insert rolls that employee back.
 * Reported as "created 0" with no reason, that reads as the feature being broken rather than as the
 * department already being configured, which is the question this text exists to answer.
 */
export function summariseFailures(
	failures: FailedRecurringShift[],
	nameByNurseId: Map<number, string>,
): { conflicts: string[]; other: string[]; text: string } | null {
	if (failures.length === 0) return null;
	// Name them. "12 employees already had shifts" tells an operator the run was partial but not who
	// to go and look at, which is the only thing they can act on — and with per-employee rollback,
	// WHICH employees failed is exactly the information the result is carrying.
	const nameOf = (failure: FailedRecurringShift) => nameByNurseId.get(failure.nurseId) ?? `#${failure.nurseId}`;
	const conflicts = failures.filter((failure) => /conflict shift|ConflictShift/i.test(failure.detail)).map(nameOf);
	const other = failures.filter((failure) => !/conflict shift|ConflictShift/i.test(failure.detail)).map(nameOf);
	const parts: string[] = [];
	if (conflicts.length > 0) {
		parts.push(
			`${conflicts.length} already had shifts in the slots the new template wanted, so nothing was changed for them — almost always because they already have a recurring template.`,
		);
	}
	if (other.length > 0) parts.push(`${other.length} failed for another reason.`);
	return { conflicts, other, text: parts.join(" ") };
}

/** Up to `limit` names, then "and N more" — a result banner is not the place for a list of 200. */
export function nameList(names: string[], limit = 8): string {
	if (names.length <= limit) return names.join(", ");
	return `${names.slice(0, limit).join(", ")} and ${names.length - limit} more`;
}

export type NamedSelectedOutcome = RecurringSelectedOutcome & { nurseName: string };

/**
 * One approval's per-employee outcomes, each carrying a name rather than an id, plus whatever about
 * the result does not add up.
 *
 * The accounting is checked but NOT thrown on. Approval is a destructive write that has already
 * committed by the time this renders — it retires templates and creates others — so throwing would
 * take the results panel to the error boundary and leave the operator with no record of what just
 * happened to the schedule. The inconsistency is reported in the banner instead, which is louder in
 * the only way that matters: the person looking at it sees it.
 *
 * Pure, and out here rather than inline in the section, so the reporting of a destructive write can
 * be tested without mounting a page's worth of providers.
 */
export function summariseApprovalOutcomes(
	creation: RecurringCreationResult | null,
	nameByNurseId: Map<number, string>,
): { namedOutcomes: NamedSelectedOutcome[]; accountingWarning: string | null } {
	if (!creation) return { namedOutcomes: [], accountingWarning: null };

	const duplicated = creation.selectedOutcomes.length - new Set(creation.selectedOutcomes.map((outcome) => outcome.candidateId)).size;
	const unaccounted = creation.approvedCount - creation.selectedOutcomes.length;
	const namedOutcomes = creation.selectedOutcomes.map((outcome) => ({
		...outcome,
		// An id is worse than a name but still identifies the person, and a run can report on an
		// employee the current proposal no longer lists.
		nurseName: nameByNurseId.get(outcome.nurseId) ?? `Employee ${outcome.nurseId}`,
	}));

	const problems = [
		unaccounted !== 0 ? `${creation.selectedOutcomes.length} of ${creation.approvedCount} selected employees were accounted for` : null,
		duplicated > 0 ? `${duplicated} ${duplicated === 1 ? "employee was" : "employees were"} reported more than once` : null,
	].filter((problem): problem is string => problem !== null);

	return { namedOutcomes, accountingWarning: problems.length > 0 ? problems.join("; ") : null };
}
